import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import StepTitle from './StepTitle';
import { INSTANCE_CATEGORY, PAYMENT_TYPES } from '../../../constants';
import { PRIVATE_ROUTE, buildPath } from '../../../routes/routes';
import { projectSetupComplete } from '../../../utils/paymentUtils';
import Text from '../../elements/text/Text';
import CreditCard from '../../sections/credit-card-display/CreditCard';
import ProjectSetupIncomplete from '../../sections/project-setup-incomplete/ProjectSetupIncomplete';

const Payment = ({ projectId, payment }) => {
  const { t } = useTranslation();

  const { getValues } = useFormContext();

  const { cards, billingInfo, managers } = payment;

  const setupComplete = projectSetupComplete(cards, billingInfo, managers);
  const isInvoicePayment = billingInfo?.paymentType === PAYMENT_TYPES.INVOICE;
  const isCommunity = getValues('plan') === INSTANCE_CATEGORY.COMMUNITY;

  return isCommunity ? (
    <Box variant="outlined" sx={{ p: '2em', textAlign: 'center' }}>
      <Text gutterBottom bodyBold>
        {t('communityCreate', { ns: 'descriptions' })}
      </Text>
      <Text gutterBottom bodyBold>
        {t('noPaymentNeeded', { ns: 'descriptions' })}
      </Text>
      <Text gutterBottom bodyBold>
        {t('deleteAfterThirty', { ns: 'descriptions' })}
      </Text>
    </Box>
  ) : setupComplete ? (
    <section>
      <StepTitle title={t('confirmPaymentMethod', { ns: 'descriptions' })} />
      {isInvoicePayment ? (
        <Box sx={{ my: 2 }}>
          <Text align="center" gutterBottom bodyBold>
            {t('chargeByInvoice', { ns: 'descriptions' })}
          </Text>
          <Text align="center">
            {t('invoiceSentByMail', { ns: 'descriptions' })}
          </Text>
        </Box>
      ) : (
        <Grid container justifyContent="center">
          <Grid style={{ marginBottom: '1em' }} size={12}>
            <Text align="center" gutterBottom>
              {t('cardCharged', { ns: 'descriptions' })}
            </Text>
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 7,
              md: 4,
              xl: 3,
            }}
          >
            <CreditCard card={cards} />
          </Grid>

          <Grid style={{ marginTop: '1em' }} size={12}>
            <Text align="center" component="p" caption>
              <Trans
                i18nKey="changePaymentMethod"
                ns="descriptions"
                components={[
                  <Link
                    key="0"
                    aria-label="to billing page"
                    component={RouterLink}
                    to={buildPath({
                      path: PRIVATE_ROUTE.PROJECT_BILLING,
                      params: { projectId: projectId },
                    })}
                    underline="hover"
                  />,
                ]}
              />
            </Text>
          </Grid>
        </Grid>
      )}
    </section>
  ) : (
    <ProjectSetupIncomplete
      serviceName={t('paidInstance', { ns: 'instance' })}
      cards={cards}
      billingInfo={billingInfo}
      managers={managers}
    />
  );
};

Payment.propTypes = {
  projectId: PropTypes.string.isRequired,
  payment: PropTypes.object.isRequired,
};

export default Payment;
